import React, { useContext } from "react"
import styled from "styled-components"
import AudioLevelIndicator from "~/components/twilio/AudioLevelIndicator"
import PinIcon from "~/components/twilio/PinIcon"
import { breakpoint } from "~/utils/functions"
import { Theme } from "~/constants/theme"
import AvatarIcon from "~/components/twilio/AvatarIcon"
import { useCallVideoState } from "~/composable/useCallVideoStateZoom"
import { ParticipantsContext } from "./ParticipantsProvider"
import { VideoContext } from "./VideoProvider"
import type { Participant } from "@zoom/videosdk"

const ParticipantInfoContainer = styled.div`
  & {
    width: 225px;
    height: 135px;
    position: relative;
    display: flex;
    align-items: center;
    overflow: hidden;
    margin-bottom: 15px;

    & video {
      filter: none;
      object-fit: contain !important;
    }

    border-radius: 4px;

    border: ${Theme.participantBorderWidth}px solid rgb(245, 248, 255);
    background: black;

    @media screen and (max-width: 520px) {
      width: 170px;
      height: 110px;
      font-size: 12px;
    }

    @media only screen and (max-width: 380px) {
      width: 155px;
      height: 100px;
    }
  }

  .innerContainer {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
  }

  .infoContainer {
    position: absolute;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    width: 100%;
    background: transparent;
    top: 0px;
  }

  .avatarContainer {
    display: flex;
    align-items: center;
    justify-content: center;
    background: black;
    position: absolute;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    z-index: 1;

    ${breakpoint("sm")} {
      & svg {
        transform: scale(0.7);
      }
    }
  }

  .reconnectingContainer {
    position: absolute;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(40, 42, 43, 0.75);
    z-index: 1;
  }

  .screenShareIconContainer {
    background: rgba(0, 0, 0, 0.5);
    padding: 0.18em 0.3em;
    margin-right: 0.3em;
    display: flex;

    & path {
      fill: white;
    }
  }

  .identity {
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 0.18em 0.8em 0.18em 0.5em;
    margin: 0px;
    display: flex;
    align-items: center;
  }

  .infoRowBottom {
    display: flex;
    justify-content: space-between;
    position: absolute;
    bottom: 10px;
    left: 10px;
    @media screen and (max-width: 520px) {
      bottom: 5px;
      left: 5px;
    }
  }

  .typeography {
    color: white;

    ${breakpoint("sm")} {
      font-size: 0.75rem;
    }
  }

  &.hideParticipant {
    display: none;
  }

  &.cursorPointer {
    cursor: pointer;
  }
`

const Typography = styled.p`
  font-size: 10px;
`

interface ParticipantInfoProps {
  user: Participant
  children: React.ReactNode
}

export default function ParticipantInfo({ user, children }: ParticipantInfoProps) {
  const { selectParticipant, handleSelectParticipant } = useContext(ParticipantsContext)
  const { client } = useContext(VideoContext)
  const currentUser = client?.getCurrentUserInfo()
  const isMe = currentUser?.userId === user?.userId
  const { participantList } = useCallVideoState()
  const userTypeName = participantList.find((item) => item.userId === user?.userId)?.userType || ""

  const isVideoSwitchedOff = !user.bVideoOn
  const isSelected = selectParticipant?.userId === user.userId

  return (
    <ParticipantInfoContainer
      className={!isMe ? "cursorPointer" : ""}
      onClick={() => !isMe && handleSelectParticipant(user)}
      data-cy-participant={user?.userId}
    >
      <div className={"infoContainer"}>
        <div className={"infoRowBottom"}>
          <span className={"identity"}>
            <AudioLevelIndicator user={user} />
            <Typography variant="body1" className={"typeography"} component="span">
              {userTypeName}
            </Typography>
          </span>
        </div>
        <div>{isSelected && <PinIcon />}</div>
      </div>

      <div className={"innerContainer"}>
        {isVideoSwitchedOff && (
          <div className={"avatarContainer"}>
            <AvatarIcon />
          </div>
        )}

        {/* HungHV: zoom has no event to catch participants' connect status */}
        {/* {isParticipantReconnecting && (
          <div className={"reconnectingContainer"}>
            <Typography variant="body1" className={"typeography"}>
              Reconnecting...
            </Typography>
          </div>
        )} */}
        {children}
      </div>
    </ParticipantInfoContainer>
  )
}
